<template>
  <!-- Wait for permissions before rendering to avoid a 403 flash -->
  <div v-if="!permsLoaded"></div>

  <PageError
    v-else-if="!canViewHostAvailability && !user?.is_superuser"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />

  <PageError v-else-if="error" :error="error" :errorCode="errorCode" />

  <div v-else class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Host Availability - {{ filteredItems.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>

      <div class="mb-4 text-base flex space-x-3 justify-between">
        <div class="relative group">
          <button
            type="button"
            class="hover:bg-transparent bg-transparent"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
          </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
        <button
          v-if="canAddHostAvailability || user?.is_superuser"
          id="add-host-availability-btn"
          @click="goToAdd"
          class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Host Availability
        </button>
      </div>
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading Host Availability..."
      @select="handleOrgSelect"
    />

    <!-- Search and Bulk Action Section -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar v-model="searchQuery" placeholderText="Search by job name or host..." />
      </div>

      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="canDeleteHostAvailability || user?.is_superuser ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedItems.length === 0"
          :class="[
            'btn focus:outline-none focus:ring-2 focus:ring-blue-500',
            bulkAction && selectedItems.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Table -->
    <GenericTable
      id="host-availability-table"
      ref="table"
      :columns="columns"
      :items="paginatedItems"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedItems"
      @rowClick="goToDetail"
    >
      <template #last_modified="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0">
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="selectedObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem = false"
      :isChildObject="true"
      :count="count"
    />
  </div>
</template>

<script>
import { getAllHostAvailabilities, deleteHostAvailability } from "@/services/hostAvailabilityService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import { formatError, errorMessages } from "@/utils/errorMessages";
import { useStore } from "@/store";
import DeleteModal from "@/components/DeleteModal.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import GlassSelect from "@/components/GlassSelect.vue";

export default {
  name: "HostAvailabilityView",
  components: {
    SearchBar,
    GenericTable,
    PageError,
    OrganizationTabs,
    DeleteModal,
    GlassSelect,
  },
  data() {
    return {
      items: [],
      searchQuery: "",
      filteredItems: [],
      currentPage: 1,
      itemsPerPage: 10,
      selectedItems: [],
      deletingItem: false,
      bulkAction: "",
      columns: [
        { key: "job_name", label: "Job Name", sortable: true, width: "35%" },
        { key: "host", label: "Host", sortable: true, width: "25%" },
        { key: "module", label: "Module", sortable: true, width: "18%" },
        { key: "last_modified", label: "Last Modified", sortable: true, width: "22%" },
      ],
      errorMessages,
      error: "",
      errorCode: "",
      isDeleting: false,
      hostAvailabilityTutorialSteps: [
        {
          element: '#searchbar',
          action: 'display',
          message: 'Search host availability checks by job name or host.'
        },
        {
          element: '#add-host-availability-btn',
          action: 'display',
          message: 'Click here to create a new host availability check.'
        },
        {
          element: '#host-availability-table',
          action: 'display',
          message: 'Click any row to view or edit host availability details.'
        },
      ],
    };
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    canViewHostAvailability() {
      return useStore.hasPermission("Can view Host Availability");
    },
    canAddHostAvailability() {
      return useStore.hasPermission("Can add Host Availability");
    },
    canDeleteHostAvailability() {
      return useStore.hasPermission("Can delete Host Availability");
    },
    isServiceProviderUser() {
      return useStore.isServiceProviderUser();
    },
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredItems.length / this.itemsPerPage) || 1;
    },
    paginatedItems() {
      if (this.itemsPerPage === -1) return this.filteredItems;
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredItems.slice(start, start + this.itemsPerPage);
    },
    selectedObjects() {
      return this.selectedItems.map((item) => ({
        name: "Host Availability",
        value: item.job_name,
      }));
    },
    count() {
      return [{ name: "Host Availabilities", value: this.selectedObjects.length }];
    },
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    permsLoaded: {
      handler(loaded) {
        // Only fetch once we know the user may view, so a denied user never hits the API.
        if (loaded && (this.canViewHostAvailability || this.user?.is_superuser)) {
          this.initData();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async startTutorial() {
      await this._start(this.hostAvailabilityTutorialSteps);
    },
    async initData() {
      await this.tenant.fetchOrganizations();
      if (this.tenant.currentOrganizationId.value) {
        await this.tenant.syncUrlParam();
        await this.fetchItems(this.tenant.currentSchemaName.value);
      } else {
        await this.tenant.loadInitialData(this.fetchItems);
      }
    },
    async handleOrgSelect(orgId) {
      try {
        this.error = "";
        await this.tenant.switchOrganization(orgId, this.fetchItems);
      } catch (error) {
        this.error = "Failed to switch organization";
        console.error("Organization select error:", error);
      }
    },
    async fetchItems(schemaName = null) {
      try {
        this.items = await getAllHostAvailabilities(schemaName);
        this.applyFilters();
      } catch (error) {
        console.error("Error fetching host availabilities:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    goToDetail(item) {
      this.$router.push({
        name: "HostAvailabilityDetails",
        params: { id: item.id },
        query: this.tenant.addSchemaParam(),
      });
    },
    goToAdd() {
      this.$router.push({
        name: "AddHostAvailability",
        query: this.tenant.addSchemaParam(),
      });
    },
    updateSelectedItems(selectedItems) {
      this.selectedItems = selectedItems.filter((item) => !item.marked_for_deletion);
    },
    applyBulkAction() {
      if (this.bulkAction === "delete") this.deletingItem = true;
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true;
      await this.deleteSelectedItems();
      this.deletingItem = false;
      this.bulkAction = "";
    },
    async deleteSelectedItems() {
      if (this.selectedItems.length === 0) return;
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to delete host availabilities!";
        this.errorCode = "422!";
        return;
      }
      try {
        await Promise.all(
          this.selectedItems.map((item) =>
            deleteHostAvailability(item.id, validation.client)
          )
        );
        await this.fetchItems(validation.client);
        this.$refs.table.clearSelection();
        this.selectedItems = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting host availabilities. Please try again.");
      } finally {
        this.isDeleting = false;
      }
    },
    applyFilters() {
      const query = (this.searchQuery || "").toLowerCase();
      this.filteredItems = (this.items || []).filter((item) => {
        const jobName = (item?.job_name || "").toLowerCase();
        const host = (item?.host || "").toLowerCase();
        return jobName.includes(query) || host.includes(query);
      });
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
  },
};
</script>
